import { activityAllowed, type ObjectBehavior } from '@dataroom/core/object-behavior';
// Owner: engagements (owner feedback F19). Extension point: the quick actions "Book a meeting / Write an e-mail /
// Log a call / Add a note / Add a task" shown in the header of every record and object, on Home, in the Inbox
// and in the command bar. Contribution files: `src/features/**/quick-actions.ts`, default export
// `defineQuickAction(…)` or an array. Host: engagements.
import type { IconName } from '@dataroom/ui';
import { createExtensionRegistry, hasFunction, hasShape } from './registry';

export type QuickActionContext = {
  /** The record the action starts from (a record's header, a task about a record). */
  record?: {
    objectKey: string;
    recordId: string;
    title: string;
    /** The record's own address when its object has an e-mail field (null when it is empty): it is a person-like record. */
    email?: string | null;
    /** Addresses a message to this record goes to (its own, else the linked people's). */
    recipients?: string[];
    /** Traits of the record's object (`member_directory` …): an action may not fit every kind of record. */
    traits?: readonly string[];
    behavior?: ObjectBehavior;
  };
  /** False while no permissible manually creatable task type exists for the record/object. */
  canCreateTask?: boolean;
  /** An object's list: the action asks for a record of this object first. */
  objectKey?: string;
  behavior?: ObjectBehavior;
  /** Where it renders. */
  placement: 'record' | 'object' | 'home' | 'inbox' | 'command';
};

export type QuickActionDefinition = {
  /** Unique, dotted: `engagements.call`, `meetings.book`. */
  id: string;
  title: () => string;
  icon: IconName;
  /** Lower first: meeting 100, e-mail 200, call 300, note 400, task 500. */
  order?: number;
  /** Words the command bar matches (any language). */
  keywords?: () => string[];
  /**
   * The words people say for exactly this action ("call", "note", "e-mail"): a command bar query that equals one of
   * them or starts it ranks the action above every other hit (it is the intent, not a search).
   */
  verbs?: () => string[];
  /** Hidden when false (permissions). */
  visible?: (ctx: QuickActionContext) => boolean;
  run: (ctx: QuickActionContext) => void | Promise<void>;
};

export function defineQuickAction(def: QuickActionDefinition): QuickActionDefinition {
  return def;
}

export const quickActions = createExtensionRegistry<QuickActionDefinition>({
  point: 'quick-actions',
  glob: 'features/**/quick-actions.ts',
  host: 'engagements',
  modules: import.meta.glob('../features/**/quick-actions.ts', { eager: true }),
  key: (a) => a.id,
  check: (v): v is QuickActionDefinition => hasShape(v, ['id', 'icon'], ['title']) && hasFunction(v, 'run'),
});

/** The quick actions that apply here, in display order. */
export function quickActionsFor(ctx: QuickActionContext): QuickActionDefinition[] {
  return quickActions
    .list()
    .filter((a) => {
      const behavior = ctx.record?.behavior ?? ctx.behavior;
      const kind = a.id.startsWith('engagements.') ? a.id.slice('engagements.'.length) : null;
      return (
        (!behavior || !kind || activityAllowed({ traits: ctx.record?.traits ?? [], behavior }, kind)) &&
        (kind !== 'task' || ctx.canCreateTask !== false) &&
        (a.visible?.(ctx) ?? true)
      );
    })
    .sort((a, b) => (a.order ?? 1000) - (b.order ?? 1000));
}
